"use client"

import { useRef, useState, useEffect } from "react"
import { Input } from "../../ui/input"
import { Button } from "../../ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card"
import { ScrollArea } from "../../ui/scroll-area"
import { Badge } from "../../ui/badge"
import { toast } from "sonner"
import { post, get } from "../../../lib/api"
import JSZip from "jszip"
import { readFileWithEncodingDetection, readZipFileWithEncodingDetection } from "../../../lib/encoding-utils"

// API function to get the next available date
const getNextAvailableDate = async (game_id) => {
    try {
        const response = await get(`/challenges?method=getLastDate&game_id=${game_id}`)

        if (response.error) {
            throw new Error(response.error)
        }

        // Calculate next available date from the last date
        const lastDate = response.lastDate
        if (lastDate) {
            const nextDate = new Date(lastDate)
            nextDate.setDate(nextDate.getDate() + 1)
            return { nextAvailableDate: nextDate.toISOString().split("T")[0] }
        } else {
            // If no last date, use today
            return { nextAvailableDate: new Date().toISOString().split("T")[0] }
        }
    } catch (error) {
        console.error("API call failed:", error)
        throw error
    }
}

// Helper function to extract filename without folder path
const getFileNameFromPath = (filePath) => {
    // Remove folder paths (handles both forward and backward slashes)
    const fileName = filePath.split(/[/\\]/).pop()
    return fileName
}

const ConnectionsUploader = ({ game_id, onUpdate }) => {
    const [uploadedFiles, setUploadedFiles] = useState([])
    const [currentFileIndex, setCurrentFileIndex] = useState(0)
    const [nextAvailableDate, setNextAvailableDate] = useState(null)
    const [isLoadingDate, setIsLoadingDate] = useState(false)
    const [uploadMode, setUploadMode] = useState("single") // "single" or "bulk"
    const fileInputRef = useRef(null)

    // Fetch next available date on component mount and after each upload
    useEffect(() => {
        fetchNextAvailableDate()
    }, [game_id])

    const fetchNextAvailableDate = async () => {
        setIsLoadingDate(true)
        try {
            const response = await getNextAvailableDate(game_id)
            setNextAvailableDate(response.nextAvailableDate)

            // Update all unconfirmed files with new dates
            setUploadedFiles((prev) => {
                if (prev.length === 0) return prev

                const updatedFiles = prev.map((file, index) => {
                    if (!file.dateConfirmed && !file.uploaded) {
                        const newDate = getNextDate(response.nextAvailableDate, index)
                        return { ...file, selectedDate: newDate }
                    }
                    return file
                })
                return updatedFiles
            })
        } catch (error) {
            console.error("Failed to fetch next available date:", error)
            toast.error("Failed to fetch next available date")
            // Fallback to today's date
            setNextAvailableDate(new Date().toISOString().split("T")[0])
        } finally {
            setIsLoadingDate(false)
        }
    }

    const getNextDate = (dateString, daysToAdd = 0) => {
        const date = new Date(dateString)
        date.setDate(date.getDate() + daysToAdd)
        return date.toISOString().split("T")[0]
    }

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files || [])
        if (files.length === 0) return

        const file = files[0] // We only handle one file at a time (either .txt or .zip)

        if (file.name.endsWith(".zip")) {
            await handleZipFile(file)
        } else if (file.type === "text/plain" || file.name.endsWith(".txt")) {
            await handleSingleTxtFile(file)
        } else {
            toast.error("Please upload a .txt file or a .zip file containing .txt files.")
            return
        }
    }

    const handleSingleTxtFile = async (file) => {
        setUploadMode("single")

        const fileContent = await readFileContent(file)
        const fileName = file.name
        const nameWithoutExtension = fileName.endsWith(".txt") ? fileName.slice(0, -4) : fileName

        // Use next available date as default
        const defaultDate = nextAvailableDate || new Date().toISOString().split("T")[0]

        const processedFile = {
            id: `${Date.now()}`,
            file,
            fileName,
            challengeName: nameWithoutExtension,
            fileContent,
            selectedDate: defaultDate,
            dateConfirmed: false,
            uploaded: false,
        }

        setUploadedFiles([processedFile])
        setCurrentFileIndex(0)
        toast.success("File loaded successfully!")
    }

    const handleZipFile = async (zipFile) => {
        setUploadMode("bulk")

        try {
            const zip = new JSZip()
            const zipContent = await zip.loadAsync(zipFile)

            const txtFiles = []

            // Extract all .txt files from the zip
            for (const [filename, file] of Object.entries(zipContent.files)) {
                if (!file.dir && filename.endsWith(".txt")) {
                    const content = await readZipFileWithEncodingDetection(file)
                    txtFiles.push({
                        filename,
                        content,
                    })
                }
            }

            if (txtFiles.length === 0) {
                toast.error("No .txt files found in the zip archive.")
                return
            }

            // Process each txt file
            const processedFiles = []
            const baseDate = nextAvailableDate || new Date().toISOString().split("T")[0]

            for (let i = 0; i < txtFiles.length; i++) {
                const txtFile = txtFiles[i]
                const cleanedContent = cleanFileContent(txtFile.content)

                // Extract just the filename without folder path
                const fileNameOnly = getFileNameFromPath(txtFile.filename)
                const nameWithoutExtension = fileNameOnly.endsWith(".txt") ? fileNameOnly.slice(0, -4) : fileNameOnly

                // Calculate date for this file (sequential dates starting from next available)
                const fileDate = getNextDate(baseDate, i)

                processedFiles.push({
                    id: `${Date.now()}-${i}`,
                    fileName: fileNameOnly, // Use filename without folder path
                    challengeName: nameWithoutExtension,
                    fileContent: cleanedContent,
                    selectedDate: fileDate,
                    dateConfirmed: false,
                    uploaded: false,
                })
            }

            setUploadedFiles(processedFiles)
            setCurrentFileIndex(0)
            toast.success(`${txtFiles.length} files extracted from zip and processed!`)
        } catch (error) {
            console.error("Error processing zip file:", error)
            toast.error("Failed to process zip file. Please ensure it's a valid zip archive.")
        }
    }

    const readFileContent = async (file) => {
        try {
            const text = await readFileWithEncodingDetection(file)
            const cleanedContent = cleanFileContent(text)
            return cleanedContent
        } catch (error) {
            console.error("File reading error:", error)
            throw error
        }
    }

    const cleanFileContent = (content) => {
        const lines = content.split("\n")
        const cleanedLines = []
        let blankLineInserted = false

        for (let i = 0; i < lines.length; i++) {
            const trimmed = lines[i].trim()
            if (!trimmed) {
                if (!blankLineInserted) {
                    cleanedLines.push("")
                    blankLineInserted = true
                }
                continue
            }
            const [group, ...rest] = trimmed.split(/\s+/)
            cleanedLines.push(group + rest.join(" "))
        }

        return cleanedLines.join("\n")
    }

    const updateFile = (index, updates) => {
        setUploadedFiles((prev) => prev.map((file, i) => (i === index ? { ...file, ...updates } : file)))
    }

    const handleDateConfirm = (index) => {
        const file = uploadedFiles[index]
        if (!file.selectedDate) {
            toast.error("Please select a date.")
            return
        }
        updateFile(index, { dateConfirmed: true })
        toast.success(`Date confirmed for ${file.fileName}`)
    }

    const handleDateChange = (index, newDate) => {
        updateFile(index, { selectedDate: newDate, dateConfirmed: false })
    }

    const uploadSingleFile = async (index) => {
        const file = uploadedFiles[index]

        if (!file.fileContent || file.challengeName.trim() === "") {
            toast.error("Please provide a challenge name and valid file content.")
            return
        }

        if (!file.dateConfirmed) {
            toast.error("Please confirm the upload date first.")
            return
        }

        try {
            // First POST request - Add the challenge
            const response = await post(
                {
                    method: "addChallengeNoDup",
                    game_id,
                    challenge_id: file.challengeName,
                    data: file.fileContent,
                    variant_id: 1,
                    variant_name: "challenge",
                },
                "/challenges",
            )

            if (response.error) {
                if (response.error === "Failed to fetch") {
                    toast.error(`Challenge "${file.challengeName}" already exists.`)
                } else {
                    toast.error(response.error)
                }
                return
            }

            // Second POST request - Schedule the challenge for the selected date
            const dailyResponse = await post(
                {
                    method: "addDailyChallenge",
                    game_id,
                    date: file.selectedDate,
                    daily_challenge_info: file.challengeName,
                },
                "/challenges",
            )

            if (dailyResponse.error) {
                toast.error(`Challenge uploaded but scheduling failed: ${dailyResponse.error}`)
                return
            }

            updateFile(index, { uploaded: true })
            toast.success(`"${file.fileName}" uploaded and scheduled for ${formatDateDisplay(file.selectedDate)}!`)
            onUpdate?.()

            // Refresh the next available date after successful upload
            await fetchNextAvailableDate()
        } catch (err) {
            toast.error(`Upload failed for "${file.fileName}"!`)
            console.error(err)
        }
    }

    const uploadAllConfirmed = async () => {
        const confirmedFiles = uploadedFiles.filter((file) => file.dateConfirmed && !file.uploaded)

        if (confirmedFiles.length === 0) {
            toast.error("No confirmed files to upload.")
            return
        }

        toast.info(`Uploading ${confirmedFiles.length} files...`)

        for (const file of confirmedFiles) {
            const index = uploadedFiles.findIndex((f) => f.id === file.id)
            await uploadSingleFile(index)
        }
    }

    const confirmAllDates = () => {
        setUploadedFiles((prev) => prev.map((file) => ({ ...file, dateConfirmed: true })))
        toast.success("All dates confirmed!")
    }

    const formatDateDisplay = (dateString) => {
        return new Date(dateString).toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        })
    }

    const clearAll = () => {
        setUploadedFiles([])
        setCurrentFileIndex(0)
        setUploadMode("single")
        if (fileInputRef.current) {
            fileInputRef.current.value = ""
        }
    }

    const currentFile = uploadedFiles[currentFileIndex]
    const confirmedCount = uploadedFiles.filter((f) => f.dateConfirmed).length
    const uploadedCount = uploadedFiles.filter((f) => f.uploaded).length

    return (
        <div className="space-y-6 p-6 border-2 border-gray-200 rounded-lg bg-white shadow-sm">
            {/* Header with stats */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <h3 className="text-xl font-bold text-gray-900">Connections Uploader</h3>
                    {isLoadingDate && <span className="text-sm text-blue-600 font-medium">Loading next date...</span>}
                    {nextAvailableDate && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 font-medium">
                            Next available: {formatDateDisplay(nextAvailableDate)}
                        </Badge>
                    )}
                </div>
                {uploadedFiles.length > 0 && (
                    <div className="flex items-center gap-3">
                        <Badge variant="secondary" className="bg-gray-100 text-gray-800 font-medium">
                            {uploadedFiles.length} file{uploadedFiles.length !== 1 ? "s" : ""}
                        </Badge>
                        <Badge variant="default" className="bg-blue-600 text-white font-medium">
                            {confirmedCount} confirmed
                        </Badge>
                        <Badge className="bg-green-600 text-white font-medium">{uploadedCount} uploaded</Badge>
                    </div>
                )}
            </div>

            {/* File Upload */}
            <div className="space-y-3">
                <Input
                    type="file"
                    accept=".txt,.zip"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                />
                <div className="flex gap-3">
                    <Button
                        className="bg-purple-700 hover:bg-purple-800 text-white font-medium px-6 py-2 shadow-sm"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Upload .txt File or .zip Archive
                    </Button>
                    {uploadedFiles.length > 0 && (
                        <>
                            {uploadMode === "bulk" && (
                                <Button
                                    onClick={confirmAllDates}
                                    variant="outline"
                                    className="border-blue-300 text-blue-700 hover:bg-blue-50 font-medium bg-transparent"
                                >
                                    Confirm All Dates
                                </Button>
                            )}
                            <Button onClick={uploadAllConfirmed} className="bg-green-600 hover:bg-green-700 text-white font-medium">
                                Upload {uploadMode === "single" ? "File" : `All Confirmed (${confirmedCount})`}
                            </Button>
                            <Button
                                onClick={clearAll}
                                variant="outline"
                                className="border-red-300 text-red-700 hover:bg-red-50 font-medium bg-transparent"
                            >
                                Clear All
                            </Button>
                        </>
                    )}
                </div>
                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                    📁 Upload a single .txt file or a .zip archive containing multiple .txt files
                </p>
            </div>

            {uploadedFiles.length > 0 && (
                <>
                    {uploadMode === "single" ? (
                        // Single File Mode
                        <Card className="border-2 border-gray-200 shadow-sm">
                            <CardHeader className="bg-gray-50 border-b">
                                <CardTitle className="text-lg font-semibold text-gray-900">Single File Upload</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-5 p-6">
                                {/* Challenge Name Input */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700">Challenge Name</label>
                                    <Input
                                        value={currentFile.challengeName}
                                        onChange={(e) => updateFile(0, { challengeName: e.target.value })}
                                        placeholder="Enter challenge name"
                                        className="border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                                    />
                                </div>

                                {/* Date Selection */}
                                <div className="space-y-3">
                                    <label className="text-sm font-medium text-gray-700">Upload Date</label>
                                    <div className="flex items-center space-x-3">
                                        <Input
                                            type="date"
                                            value={currentFile.selectedDate}
                                            onChange={(e) => handleDateChange(0, e.target.value)}
                                            className="flex-1 border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                                        />
                                        {!currentFile.dateConfirmed ? (
                                            <Button
                                                onClick={() => handleDateConfirm(0)}
                                                variant="outline"
                                                className="whitespace-nowrap border-blue-300 text-blue-700 hover:bg-blue-50 font-medium"
                                            >
                                                Confirm Date
                                            </Button>
                                        ) : (
                                            <Button
                                                onClick={() => updateFile(0, { dateConfirmed: false })}
                                                variant="outline"
                                                className="whitespace-nowrap bg-green-50 border-green-300 text-green-700 hover:bg-green-100 font-medium"
                                            >
                                                ✓ Confirmed
                                            </Button>
                                        )}
                                    </div>
                                    {currentFile.dateConfirmed && (
                                        <p className="text-sm text-green-700 bg-green-50 p-2 rounded-md font-medium">
                                            📅 Scheduled for: {formatDateDisplay(currentFile.selectedDate)}
                                        </p>
                                    )}
                                </div>

                                {/* File Preview */}
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-gray-700">File Preview</label>
                                    <textarea
                                        className="w-full p-3 border-2 border-gray-300 rounded-md bg-gray-50 text-gray-900 max-h-60 overflow-auto whitespace-pre-wrap text-sm font-mono focus:border-purple-500 focus:ring-purple-500"
                                        rows={10}
                                        value={currentFile.fileContent}
                                        onChange={(e) => updateFile(0, { fileContent: e.target.value })}
                                        placeholder="Preview will appear here..."
                                    />
                                </div>

                                {/* Upload Button */}
                                <Button
                                    className={`w-full font-medium py-3 ${
                                        currentFile.uploaded
                                            ? "bg-green-600 hover:bg-green-700 text-white"
                                            : currentFile.dateConfirmed
                                                ? "bg-purple-700 hover:bg-purple-800 text-white"
                                                : "bg-gray-400 text-gray-600 cursor-not-allowed"
                                    }`}
                                    onClick={() => uploadSingleFile(0)}
                                    disabled={!currentFile.dateConfirmed || currentFile.uploaded}
                                >
                                    {currentFile.uploaded
                                        ? "✅ Successfully Uploaded"
                                        : currentFile.dateConfirmed
                                            ? `🚀 Upload for ${new Date(currentFile.selectedDate).toLocaleDateString()}`
                                            : "⏳ Confirm Date to Enable Upload"}
                                </Button>
                            </CardContent>
                        </Card>
                    ) : (
                        // Bulk Mode (from zip)
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* File List */}
                            <Card className="border-2 border-gray-200 shadow-sm">
                                <CardHeader className="bg-gray-50 border-b">
                                    <CardTitle className="text-lg font-semibold text-gray-900">
                                        Files from Zip ({uploadedFiles.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-4">
                                    <ScrollArea className="h-80">
                                        <div className="space-y-3">
                                            {uploadedFiles.map((file, index) => (
                                                <div
                                                    key={file.id}
                                                    className={`p-4 rounded-lg cursor-pointer border-2 transition-all ${
                                                        index === currentFileIndex
                                                            ? "border-purple-400 bg-purple-50 shadow-md"
                                                            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                                                    }`}
                                                    onClick={() => setCurrentFileIndex(index)}
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-sm font-medium text-gray-900 truncate">{file.fileName}</span>
                                                        <div className="flex gap-2">
                                                            {file.uploaded && (
                                                                <Badge className="text-xs bg-green-600 text-white font-medium">✅ Uploaded</Badge>
                                                            )}
                                                            {file.dateConfirmed && !file.uploaded && (
                                                                <Badge className="text-xs bg-blue-600 text-white font-medium">✓ Confirmed</Badge>
                                                            )}
                                                            {!file.dateConfirmed && (
                                                                <Badge
                                                                    variant="secondary"
                                                                    className="text-xs bg-yellow-100 text-yellow-800 font-medium"
                                                                >
                                                                    ⏳ Pending
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="text-xs text-gray-600 mt-2 font-medium">
                                                        📅 {formatDateDisplay(file.selectedDate)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                </CardContent>
                            </Card>

                            {/* Current File Editor */}
                            {currentFile && (
                                <Card className="border-2 border-purple-200 shadow-sm">
                                    <CardHeader className="bg-purple-50 border-b border-purple-200">
                                        <CardTitle className="text-lg font-semibold text-purple-900">
                                            Editing: {currentFile.fileName} ({currentFileIndex + 1}/{uploadedFiles.length})
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4 p-6">
                                        {/* Challenge Name Input */}
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700">Challenge Name</label>
                                            <Input
                                                value={currentFile.challengeName}
                                                onChange={(e) => updateFile(currentFileIndex, { challengeName: e.target.value })}
                                                placeholder="Enter challenge name"
                                                className="border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                                            />
                                        </div>

                                        {/* Date Selection */}
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700">Upload Date</label>
                                            <div className="flex items-center space-x-2">
                                                <Input
                                                    type="date"
                                                    value={currentFile.selectedDate}
                                                    onChange={(e) => handleDateChange(currentFileIndex, e.target.value)}
                                                    className="flex-1 border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                                                />
                                                {!currentFile.dateConfirmed ? (
                                                    <Button
                                                        onClick={() => handleDateConfirm(currentFileIndex)}
                                                        variant="outline"
                                                        className="whitespace-nowrap border-blue-300 text-blue-700 hover:bg-blue-50 font-medium"
                                                        size="sm"
                                                    >
                                                        Confirm
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        onClick={() => updateFile(currentFileIndex, { dateConfirmed: false })}
                                                        variant="outline"
                                                        className="whitespace-nowrap bg-green-50 border-green-300 text-green-700 hover:bg-green-100 font-medium"
                                                        size="sm"
                                                    >
                                                        ✓ Confirmed
                                                    </Button>
                                                )}
                                            </div>
                                        </div>

                                        {/* File Preview */}
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700">File Preview</label>
                                            <textarea
                                                className="w-full p-3 border-2 border-gray-300 rounded-md bg-gray-50 text-gray-900 h-32 overflow-auto whitespace-pre-wrap text-xs font-mono focus:border-purple-500 focus:ring-purple-500"
                                                value={currentFile.fileContent}
                                                onChange={(e) => updateFile(currentFileIndex, { fileContent: e.target.value })}
                                                placeholder="Preview will appear here..."
                                            />
                                        </div>

                                        {/* Individual Upload Button */}
                                        <Button
                                            className={`w-full font-medium py-2 ${
                                                currentFile.uploaded
                                                    ? "bg-green-600 hover:bg-green-700 text-white"
                                                    : currentFile.dateConfirmed
                                                        ? "bg-purple-700 hover:bg-purple-800 text-white"
                                                        : "bg-gray-400 text-gray-600 cursor-not-allowed"
                                            }`}
                                            onClick={() => uploadSingleFile(currentFileIndex)}
                                            disabled={!currentFile.dateConfirmed || currentFile.uploaded}
                                            size="sm"
                                        >
                                            {currentFile.uploaded
                                                ? "✅ Uploaded"
                                                : currentFile.dateConfirmed
                                                    ? `🚀 Upload for ${new Date(currentFile.selectedDate).toLocaleDateString()}`
                                                    : "⏳ Confirm Date to Enable Upload"}
                                        </Button>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    )}
                </>
            )}
        </div>
    )
}

export default ConnectionsUploader
